import {
  Box,
  Divider,
  List,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LayoutContext } from "../../contexts/LayoutContext";
import SidebarItem from "./SidebarItem";
import LogoGradient from "./LogoGradient";
import LogoutModal from "../modal/LogoutModal";
import NavComponent from "./NavComponent";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../../contexts/AccountContext";
import { useAuth } from "../../contexts/UserContext";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { common } from "../../i18n";
import { APP_VERSION } from "../../constants";
import {
  CAT15_PATH,
  navLinks,
  navLinksDataEntry,
  routes,
} from "../../config/navigation";

const sidebarSmallWidth = 90;
const sidebarFullWidth = 262;
const avatarIconWidth = 44;
const imgIconWidth = 24;

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { isDataEntry, organization } = useAuth();
  const orgContext = useContext(OrganizationContext);
  const { t } = useTranslation([common]);
  const { user } = useAccount();

  const {
    openSidebar,
    setOpenSidebar,
    openSignOut,
    setOpenSignOut,
    profile,
    setProfile,
  } = useContext(LayoutContext);

  const onMouseEnter = () => !isMobile && setOpenSidebar(true);
  const onMouseLeave = () => !isMobile && setOpenSidebar(false);
  const onSignOutClick = () => setOpenSignOut(true);
  const onSignOutClose = () => setOpenSignOut(false);
  const handleCloseClick = () => setOpenSidebar(false);

  useEffect(() => {
    if (user) setProfile(user.userProfile);
  }, [user, setProfile]);

  const sidebarWidth = openSidebar ? sidebarFullWidth : sidebarSmallWidth;
  const sidebarPaddingX = (sidebarSmallWidth - avatarIconWidth) / 2;
  const logoType = openSidebar ? "full" : "small";

  // Only an SSO session carries orgType; a missing key means a native login, which keeps today's menu.
  const hideCat15 =
    !!organization &&
    "orgType" in organization &&
    organization.orgType !== "borrower" &&
    organization.orgType !== "lender";

  // Within the cat15 menu, gate children by persona: borrower sees only My Data,
  // lender sees everything except My Data.
  const borrower = orgContext?.isCat15Borrower() ?? false;
  const gateFinancedEmissions = (links: typeof navLinks) =>
    links.map((link) => {
      if (link.path !== CAT15_PATH || !link.children) return link;
      return {
        ...link,
        children: link.children.filter((child) => {
          const isMyData = child.path === "/financed-emissions/my-data";
          return borrower ? isMyData : !isMyData;
        }),
      };
    });

  // 1) SSO: hide the whole cat15 menu for non-cat15 orgs. 2) then gate children by persona.
  const visibleNavLinks = (isDataEntry() ? navLinksDataEntry : navLinks).filter(
    (link) => !(hideCat15 && link.path === CAT15_PATH),
  );

  const navComponent = gateFinancedEmissions(visibleNavLinks).map((link) => (
      <NavComponent
        key={link.path}
        link={link}
        parentOpen={openSidebar}
        index={0}
        routes={routes}
        width={avatarIconWidth}
        leftImgSize={imgIconWidth}
        leftAvatarSize={avatarIconWidth}
      />
  ));

  const drawerContent = (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
        "& .sidebar-menu": {
          "&:hover .MuiTypography-root, &.seleted .MuiTypography-root": {
            color: "primary.main",
          },
          "& .chevron": { filter: "invert(1)" },
          "&:hover .chevron, &.seleted .chevron": {
            filter:
              "invert(51%) sepia(74%) saturate(6196%) hue-rotate(153deg) brightness(94%) contrast(91%)",
          },
          "&:hover img, &.seleted img": {
            filter:
              "invert(51%) sepia(74%) saturate(6196%) hue-rotate(153deg) brightness(94%) contrast(91%)",
          },
        },
        "& .children .sidebar-menu": {
          height: "28px",
          borderRadius: "8px",
          "&:hover, &.seleted": { bgcolor: "primary.main" },
          "&:hover .MuiTypography-root, &.seleted .MuiTypography-root": {
            color: "common.white",
          },
        },
        "& .sidebar-avatar": {
          "& .MuiAvatar-root": { border: "2px solid transparent" },
          "&:hover .MuiTypography-root": { color: "primary.main" },
          "&:hover .MuiAvatar-root": {
            border: "2px solid",
            borderColor: "primary.main",
          },
        },
      }}
    >
      <LogoGradient
        logoType={logoType}
        logoWidth={logoType === "small" ? "120px" : "144px"}
        marginTop={logoType === "small" ? "15%" : "0"}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          px: `${sidebarPaddingX}px`,
          gap: "25px",
          my: "32px",
          flexGrow: 1,
        }}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 0,
            gap: "25px",
            flexGrow: 1,
          }}
        >
          {navComponent}
        </List>
        <Divider sx={{ background: "white" }} />
        <SidebarItem
          className="sidebar-avatar"
          link="/account"
          label={
            user && user.displayName ? user.displayName : user?.email ?? "User"
          }
          iconType="Avatar"
          avatarSize={avatarIconWidth}
          onToggle={() => navigate("/account")}
          endIcon="/img/signout.svg"
          onEndIconClick={onSignOutClick}
          icon={profile ?? undefined}
        />
      </Box>

      <Typography
        variant="caption"
        sx={{ opacity: 0.5, textAlign: "center", pb: 1, color: "common.white" }}
      >
        {`V.${APP_VERSION}`}
      </Typography>

      {isMobile && (
        <Box
          onClick={handleCloseClick}
          sx={{
            position: "absolute",
            right: 20,
            top: 20,
            cursor: "pointer",
            color: "common.white",
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={openSidebar}
        onClose={handleCloseClick}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : sidebarWidth,
            bgcolor: "secondary.main",
            borderRadius: isMobile ? 0 : "0 20px 20px 0",
            border: "none",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            boxShadow: isMobile ? "none" : "4px 0px 10px rgba(0,0,0,0.1)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <LogoutModal
        open={openSignOut}
        onClose={onSignOutClose}
        onSubmit={onSignOutClose}
        title={t("Sign-out")}
        body={t("Sign-out-message")}
        buttonText={t("Yes")}
      />
    </>
  );
};

export default Sidebar;
